import { useSetToast } from "@app/components/Toast";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { type FC } from "react";
import ReactMarkdown, { type Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMarkdownTextTranslations } from "./i18n";

export type MarkdownTextProps = Options & {
    tableCopy?: boolean;
};

export const MarkdownText: FC<MarkdownTextProps> = ({
    components,
    tableCopy,
    className,
    ...props
}) => {
    const { setToastSuccess } = useSetToast();
    const { t, keys } = useMarkdownTextTranslations();

    return (
        <ReactMarkdown
            className={cn(
                "flex flex-col gap-1 text-base font-regular text-left w-full max-h-fit text-neutral-700",
                className,
            )}
            remarkPlugins={[remarkGfm]}
            components={{
                table: ({ children }) => (
                    <div className="overflow-x-scroll">
                        <table className="w-fit border border-primary rounded-md">
                            {children}
                        </table>
                    </div>
                ),
                h1: ({ children }) => (
                    <Text variant="header1" className="mt-4 mb-2">
                        {children}
                    </Text>
                ),
                h2: ({ children }) => (
                    <Text variant="header2" className="mt-4 mb-2">
                        {children}
                    </Text>
                ),
                h3: ({ children }) => (
                    <Text variant="header3" className="mt-4 mb-2">
                        {children}
                    </Text>
                ),
                h4: ({ children }) => (
                    <Text
                        variant="caption-bold"
                        mt="4"
                        mb="2"
                        color="secondary"
                    >
                        {children}
                    </Text>
                ),
                th: ({ children }) => (
                    <th className="p-2 text-left font-bold border-b border-r border-primary">
                        {children}
                    </th>
                ),
                td: ({ children }) => {
                    const handleCopy = () => {
                        if (typeof children === "string") {
                            navigator.clipboard.writeText(children).then(() => {
                                setToastSuccess(t(keys.copy_success));
                            });
                        }
                    };
                    if (!tableCopy) {
                        return (
                            <td className="p-2 text-left border-b border-r border-primary">
                                {children}
                            </td>
                        );
                    }
                    return (
                        <td className="group p-2 text-left border-b border-r border-primary relative">
                            <Box>{children}</Box>
                            <Button
                                className={cn(
                                    "opacity-0 group-hover:opacity-100",
                                    "transition-opacity",
                                    "absolute top-0.5 right-0.5",
                                    "hover:bg-opacity-50",
                                )}
                                onClick={handleCopy}
                                size="xs"
                                variant="text"
                            >
                                <Button.Icon name="duplicate" />
                            </Button>
                        </td>
                    );
                },
                p: ({ children }) => (
                    <Text variant="body" className="mt-2">
                        {children}
                    </Text>
                ),
                strong: ({ children }) => (
                    <Text as="span" variant="body-bold">
                        {children}
                    </Text>
                ),
                ol: ({ children }) => (
                    <ol className="pl-5 list-decimal list-inside">
                        {children}
                    </ol>
                ),
                ul: ({ children }) => (
                    <ul className="pl-5 list-disc list-inside">{children}</ul>
                ),
                li: ({ children }) => (
                    <li className="text-base list-item mb-0.5 last:mb-0 [&>p]:inline">
                        {children}
                    </li>
                ),
                a: ({ href }) => {
                    if (!href || href.startsWith("file-")) return null;
                    if (href.startsWith("mailto:")) {
                        const email = href.split("mailto:")[1];
                        return <a href={href}>{email}</a>;
                    }

                    return (
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <a href={href} rel="noreferrer" target="_blank">
                                    {href}
                                </a>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.link_tooltip)}
                            </Tooltip.Content>
                        </Tooltip>
                    );
                },
                // we don't trust images, so we don't render them
                img: ({ alt }) => <Box>{alt} - img</Box>,
                code: (codeProps) => {
                    const position = codeProps.node?.position;
                    const startLine = position?.start?.line;
                    const endLine = position?.end?.line;
                    const isInline = startLine === endLine;

                    return isInline ? (
                        <code className="inline-block">
                            {codeProps.children}
                        </code>
                    ) : (
                        <Box className="w-full border border-secondary px-1 py-0.5 overflow-scroll">
                            <code>{codeProps.children}</code>
                        </Box>
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
};
